<template>
    <div
        class="media-library -mx-8 bg-white rounded-lg shadow overflow-hidden"
        @drop.prevent="onDrop"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @dragenter.prevent="isDragging = true"
    >
        <!-- Drag overlay -->
        <transition name="fade">
            <div v-if="isDragging" class="absolute inset-0 bg-indigo-500 bg-opacity-10 border-2 border-dashed border-indigo-400 rounded-lg z-40 flex items-center justify-center">
                <div class="bg-white rounded-xl shadow-lg px-8 py-6 text-center">
                    <i class="far fa-cloud-upload text-3xl text-indigo-500 mb-2 block"></i>
                    <span class="text-sm font-medium text-indigo-600">Laat los om te uploaden</span>
                </div>
            </div>
        </transition>

        <!-- Two columns: filters + grid on the LEFT, info/selection panel fully on the RIGHT -->
        <div class="flex flex-col lg:flex-row" :class="picker ? 'lg:h-[72vh]' : ''" style="min-height: 60vh;">
            <!-- LEFT: header + filters + grid. Hidden on mobile once an item is
                 picked, so the detail panel takes the full screen (master/detail). -->
            <div class="flex-1 flex-col min-w-0" :class="selectedItem ? 'hidden lg:flex' : 'flex'">
                <!-- Header: stats + upload button -->
                <div class="flex items-center gap-6 px-6 py-3 border-b border-gray-200 flex-shrink-0">
                    <div class="flex items-center gap-4 text-sm text-gray-500">
                        <span v-if="stats"><strong class="text-gray-700">{{ stats.total_count }}</strong> bestanden</span>
                        <span v-if="stats" class="hidden sm:inline"><strong class="text-gray-700">{{ formatSize(stats.total_size) }}</strong> totaal</span>
                        <span v-if="stats" class="hidden md:inline text-gray-400">Contentmedia: {{ stats.spatie_count }} &middot; Uploads: {{ stats.lfm_count }}</span>
                    </div>

                    <div class="ml-auto flex items-center gap-3">
                        <div v-if="uploading" class="flex items-center gap-2">
                            <div class="text-sm text-gray-600">
                                <i class="far fa-circle-notch fa-spin mr-1"></i>
                                {{ uploadedCount }}/{{ totalCount }}
                            </div>
                            <div class="w-24 bg-gray-200 rounded-full h-1">
                                <div class="bg-indigo-500 h-1 rounded-full transition-all" :style="{ width: progressPercent + '%' }"></div>
                            </div>
                        </div>

                        <label class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                            <i class="far fa-cloud-upload mr-1.5 text-gray-400"></i> Bestand kiezen
                            <input type="file" class="sr-only" @change="onFileSelect" ref="fileInput" multiple />
                        </label>
                    </div>
                </div>

                <!-- Filters -->
                <media-filters
                    :filters="filters"
                    :view-mode="viewMode"
                    :modules="filterOptions.modules"
                    :records="moduleRecords"
                    class="flex-shrink-0"
                    @filter-change="onFilterChange"
                    @view-change="onViewChange"
                ></media-filters>

                <!-- Grid / List (scrolls) -->
                <div class="flex-1 min-h-0 overflow-auto px-6 py-4" ref="scrollContainer">
                    <media-grid
                        :items="items"
                        :view-mode="viewMode"
                        :selected-item="selectedItem"
                        :selected-items="selectedItems"
                        :loading="loading"
                        :has-more="hasMore"
                        @select="onSelect"
                        @toggle-select="onToggleSelect"
                        @load-more="loadMore"
                    ></media-grid>
                </div>
            </div>

            <!-- RIGHT: info / selection panel (full height; header + actions stay pinned) -->
            <media-preview
                v-if="selectedItem"
                :item="selectedItem"
                :picker="picker"
                :detail-url="detailUrl"
                :delete-url="deleteUrl"
                @close="selectedItem = null"
                @deleted="onDeleted"
                @updated="onUpdated"
                @select="$emit('select', $event)"
            ></media-preview>
            <div
                v-else-if="picker"
                class="w-full lg:w-96 lg:flex-shrink-0 lg:h-full border-t lg:border-t-0 lg:border-l border-gray-200 flex items-center justify-center p-8 text-center text-sm text-gray-400"
            >
                Selecteer een afbeelding voor informatie.
            </div>
        </div>

        <!-- Bulk actions bar -->
        <div v-if="selectedItems.length > 1 && !picker" class="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-8 py-3 flex items-center gap-4 shadow-lg z-50">
            <span class="text-sm text-gray-600"><strong>{{ selectedItems.length }}</strong> geselecteerd</span>
            <button @click="bulkDelete" class="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                <i class="far fa-trash-alt mr-1.5"></i> Verwijderen
            </button>
            <button @click="selectedItems = []" class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ml-auto">
                Selectie opheffen
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MediaLibrary',

    props: {
        itemsUrl: { type: String, required: true },
        statsUrl: { type: String, required: true },
        uploadUrl: { type: String, required: true },
        deleteUrl: { type: String, required: true },
        detailUrl: { type: String, required: true },
        // Picker mode (canvas modal): clicking an item emits `pick` (the wrapping
        // modal owns the confirm/cancel footer). Bulk actions (Swal) are hidden.
        picker: { type: Boolean, default: false },
        // Pre-selected type filter (e.g. 'image' when opened from utils.image).
        initialType: { type: String, default: '' },
    },

    data() {
        return {
            items: [],
            stats: null,
            loading: false,
            isDragging: false,
            uploading: false,
            uploadedCount: 0,
            totalCount: 0,
            filters: {
                search: '',
                type: '',
                source: '',
                usage: '',
                module: '',
                module_id: '',
                sort: 'newest',
            },
            filterOptions: { modules: [] },
            moduleRecords: [],
            page: 1,
            lastPage: 1,
            viewMode: 'grid',
            selectedItem: null,
            selectedItems: [],
        };
    },

    computed: {
        hasMore() {
            return this.page < this.lastPage;
        },
        progressPercent() {
            if (this.totalCount === 0) return 0;
            return Math.round((this.uploadedCount / this.totalCount) * 100);
        },
    },

    created() {
        if (this.initialType) {
            this.filters.type = this.initialType;
        }
    },

    mounted() {
        this.fetchItems();
        this.fetchStats();
        this.fetchFilterOptions();
    },

    methods: {
        // Upload methods
        onDrop(e) {
            this.isDragging = false;
            var files = e.dataTransfer.files;
            if (files.length) {
                this.uploadFiles(files);
            }
        },

        onFileSelect(e) {
            var files = e.target.files;
            if (files.length) {
                this.uploadFiles(files);
            }
            this.$refs.fileInput.value = '';
        },

        uploadFiles(files) {
            var self = this;
            self.uploading = true;
            self.uploadedCount = 0;
            self.totalCount = files.length;

            var promises = [];
            for (var i = 0; i < files.length; i++) {
                promises.push(self.uploadSingleFile(files[i]));
            }

            Promise.all(promises)
                .then(function () {
                    self.uploading = false;
                    self.onUploaded();
                    Vue.toasted.show(self.totalCount + ' bestand(en) geüpload', { type: 'success' });
                })
                .catch(function (err) {
                    self.uploading = false;
                    var message = 'Upload mislukt';
                    if (err && err.response && err.response.data && err.response.data.message) {
                        message = err.response.data.message;
                    }
                    Vue.toasted.show(message, { type: 'error' });
                });
        },

        uploadSingleFile(file) {
            var self = this;
            var formData = new FormData();
            formData.append('file', file);

            return axios.post(this.uploadUrl, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }).then(function () {
                self.uploadedCount++;
            });
        },

        // Data methods
        fetchItems(append) {
            if (this.loading) return;
            this.loading = true;

            var params = {
                search: this.filters.search || undefined,
                type: this.filters.type || undefined,
                source: this.filters.source || undefined,
                usage: this.filters.usage || undefined,
                module: this.filters.module || undefined,        // "used in module"
                module_id: this.filters.module_id || undefined,  // a specific record of that module
                sort: this.filters.sort,
                page: this.page,
                per_page: 40,
            };

            axios.get(this.itemsUrl, { params: params })
                .then(function (response) {
                    if (append) {
                        this.items = this.items.concat(response.data.data);
                    } else {
                        this.items = response.data.data;
                    }
                    this.lastPage = response.data.meta.last_page;
                    this.loading = false;
                }.bind(this))
                .catch(function () {
                    this.loading = false;
                }.bind(this));
        },

        fetchStats() {
            axios.get(this.statsUrl)
                .then(function (response) {
                    this.stats = response.data;
                }.bind(this));
        },

        fetchFilterOptions() {
            var url = this.itemsUrl.replace(/\/items$/, '/filter-options');
            axios.get(url)
                .then(function (response) {
                    this.filterOptions = response.data;
                }.bind(this))
                .catch(function () {});
        },

        onFilterChange(filters) {
            // When the module changes, clear the record selection and load that
            // module's records for the cascading dropdown.
            if (Object.prototype.hasOwnProperty.call(filters, 'module')) {
                filters.module_id = '';
                this.fetchModuleRecords(filters.module);
            }

            this.filters = Object.assign({}, this.filters, filters);
            this.page = 1;
            this.selectedItem = null;
            this.selectedItems = [];
            this.fetchItems(false);
        },

        fetchModuleRecords(moduleClass) {
            if (!moduleClass) {
                this.moduleRecords = [];
                return;
            }

            var url = this.itemsUrl.replace(/\/items$/, '/module-records');
            axios.get(url, { params: { module: moduleClass } })
                .then(function (response) {
                    this.moduleRecords = response.data || [];
                }.bind(this))
                .catch(function () {
                    this.moduleRecords = [];
                }.bind(this));
        },

        onViewChange(mode) {
            this.viewMode = mode;
        },

        onSelect(item) {
            this.selectedItem = item;
            this.selectedItems = [item];
        },

        onToggleSelect(item) {
            var index = this.selectedItems.findIndex(function (i) { return i.id === item.id; });
            if (index >= 0) {
                this.selectedItems.splice(index, 1);
            } else {
                this.selectedItems.push(item);
            }
            this.selectedItem = item;
        },

        loadMore() {
            if (this.hasMore && !this.loading) {
                this.page++;
                this.fetchItems(true);
            }
        },

        onUploaded() {
            this.page = 1;
            this.fetchItems(false);
            this.fetchStats();
        },

        onDeleted(item) {
            this.items = this.items.filter(function (i) { return i.id !== item.id; });
            this.selectedItem = null;
            this.selectedItems = this.selectedItems.filter(function (i) { return i.id !== item.id; });
            this.fetchStats();
        },

        // Alt text (= media name) saved from the preview panel — keep the grid label in sync.
        onUpdated(payload) {
            var item = this.items.find(function (i) { return i.id === payload.id; });
            if (item) {
                item.name = payload.name;
            }
        },

        bulkDelete() {
            var self = this;

            Swal.fire({
                title: 'Weet je het zeker?',
                text: self.selectedItems.length + ' bestanden verwijderen?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                confirmButtonText: 'Verwijderen',
                cancelButtonText: 'Annuleren',
            }).then(function (result) {
                if (!result.isConfirmed) return;

                var payload = {
                    items: self.selectedItems.map(function (item) {
                        return { source: item.source, id: item.source_id };
                    }),
                };

                axios.delete(self.deleteUrl, { data: payload })
                    .then(function (response) {
                        var data = response.data || {};
                        // Items the server refused (in use) — keep them in the grid.
                        var blockedIds = {};
                        (data.blocked || []).forEach(function (b) { blockedIds[b.id] = true; });

                        var removeIds = self.selectedItems
                            .map(function (i) { return i.id; })
                            .filter(function (id) { return !blockedIds[id]; });

                        self.items = self.items.filter(function (i) { return removeIds.indexOf(i.id) === -1; });
                        self.selectedItems = [];
                        self.selectedItem = null;
                        self.fetchStats();

                        if (data.deleted) {
                            Vue.toasted.show(data.deleted + ' bestand(en) verwijderd', { type: 'success' });
                        }
                        if ((data.blocked || []).length) {
                            Vue.toasted.show((data.blocked.length) + ' overgeslagen — in gebruik op een pagina of in een module.', { type: 'error' });
                        }
                    });
            });
        },

        formatSize(bytes) {
            if (!bytes) return '0 B';
            var units = ['B', 'KB', 'MB', 'GB'];
            var i = 0;
            var size = bytes;
            while (size >= 1024 && i < units.length - 1) {
                size /= 1024;
                i++;
            }
            return size.toFixed(1) + ' ' + units[i];
        },
    },
};
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.2s ease, opacity 0.2s ease;
}
.slide-enter,
.slide-leave-to {
    transform: translateX(20px);
    opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
